import { useState, useRef } from 'react';
import { useRouter } from 'next/router';
import { useMutation } from '@apollo/client';
import Lottie from 'lottie-react';
import confettiAnimation from 'public/confetti.json';
// Api
import apolloStreamClient from 'api/apolloStreamClient';
import {
  PlayButtonClicked,
  PlayButtonClickedVariables,
} from 'api/agora/types/PlayButtonClicked';
import { PLAY_BUTTON_CLICKED } from 'api/agora/mutations';
// Types
import {
  GetStream_stream,
  GetStream_stream_interview_source_InterviewSourcePPVParticipant,
} from 'api/streams/types/GetStream';
import { StreamRecordDisplay } from 'helpers/streams';
import { getEnvLink } from 'helpers/routes';
import { parseMediaRecordId } from 'helpers/mediaStartTime';
// Hooks
import { useStreamRecordsForDisplay } from 'hooks/useStreamRecordsForDisplay';
import { InterviewSourceType, StreamStatus } from 'api/graphql-global-types';
// Hooks
import { useAthleteRelatedStreams } from 'hooks';
// Components
import { showToast } from 'components/common/Toast/Toast';
import Section from 'components/common3/Section/Section';
import OtherStreamsSection from 'components/Athlete/Streams/OtherStreamsSection/OtherStreamsSection';
import StreamContentSections from 'components/OrderStream/StreamContentSections/StreamContentSections';
import StreamClipHero from 'components/OrderStream/StreamClipHero/StreamClipHero';
import { HighlightClip } from 'components/OrderStream/StreamHighlights/types';
import StreamVideoPlayer from './StreamVideoPlayer/StreamVideoPlayer';
import FinishedStreamInfo from './components/FinishedStreamInfo/FinishedStreamInfo';
import VideoPlaylist from './components/VideoPlaylist/VideoPlaylist';
import TipStoreV2 from 'components/WatchStream/UnfinishedStream/components/WatcherView/components/StreamActions/TipStoreV2/TipStoreV2';
// UI3
import Modal from 'ui3/Modal/Modal';
import Button from 'ui3/Button/Button';
import Text from 'ui3/Text/Text';
// Styles
import styles from './StreamsPlaylist.module.scss';

type StreamsPlaylistProps = {
  stream: GetStream_stream;
  refetchStream: () => void;
  onVideoChange?: (videoURL: string) => void;
};

const StreamsPlaylist = ({
  stream,
  refetchStream,
  onVideoChange,
}: StreamsPlaylistProps): JSX.Element | null => {
  const router = useRouter();
  const recordsList = useStreamRecordsForDisplay(stream);
  const startRecordId = parseMediaRecordId(router.query);
  const startRecordIndex =
    startRecordId == null
      ? 0
      : recordsList.findIndex((record) => record.id === startRecordId);
  const resolvedStartIndex = startRecordIndex >= 0 ? startRecordIndex : 0;
  const [storeTipModalIsOpen, setStoreTipModalIsOpen] = useState<boolean>(
    false
  );
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [showConfetti, setShowConfetti] = useState<boolean>(false);
  const [selectedRecordId, setSelectedRecordId] = useState<
    number | undefined
  >();
  const selectedRecordIndex =
    selectedRecordId == null
      ? -1
      : recordsList.findIndex((record) => record.id === selectedRecordId);
  const currentVideoIndex =
    selectedRecordIndex >= 0 ? selectedRecordIndex : resolvedStartIndex;
  const currentVideoUrl =
    selectedRecordIndex >= 0
      ? recordsList[selectedRecordIndex]?.videoURL || ''
      : '';
  // A highlight clip playing in the hero takeover (null = normal stream hero).
  const [playingClip, setPlayingClip] = useState<HighlightClip | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const [playButtonClicked] = useMutation<
    PlayButtonClicked,
    PlayButtonClickedVariables
  >(PLAY_BUTTON_CLICKED, {
    client: apolloStreamClient,
  });

  const { streams } = useAthleteRelatedStreams({
    storeId: stream?.store?.id || '',
    currentStreamId: stream?.id,
    totalStreamsToShow: 10,
  });

  if (!recordsList?.length) {
    refetchStream();
    return null;
  }

  const handleStoreTipModalToggle = () => {
    setStoreTipModalIsOpen((prev) => !prev);
  };

  const handleCloseTipModal = () => {
    setShowCancelModal(true);
  };

  const handleDirectClose = () => {
    setStoreTipModalIsOpen(false);
    setShowConfetti(true);
  };

  const handleConfirmCancel = () => {
    setShowCancelModal(false);
    setStoreTipModalIsOpen(false);
  };

  const handleCancelCancel = () => {
    setShowCancelModal(false);
  };

  const handleConfettiComplete = () => {
    setShowConfetti(false);
  };

  const handlePlayButtonClicked = (streamId: string) => {
    try {
      playButtonClicked({
        variables: {
          streamId,
        },
      });
    } catch (error: any) {
      showToast({ message: error?.message, type: 'error' });
    }
  };

  const handleRecordClick = ({ videoURL }: StreamRecordDisplay) => {
    if (videoURL) {
      handleVideoChange(videoURL);
    }
  };

  const handleVideoChange = (videoURL: string) => {
    const index = recordsList.findIndex(
      (record) => record.videoURL === videoURL
    );
    if (index >= 0) {
      setSelectedRecordId(recordsList[index].id);
    }

    // Call analytics
    handlePlayButtonClicked(`${stream?.id}`);

    // Notify parent component to change video
    if (onVideoChange) {
      onVideoChange(videoURL);
    }
  };

  const ppvStreamId =
    stream?.interview?.source?.type === InterviewSourceType.PPVParticipant
      ? (stream?.interview
          ?.source as GetStream_stream_interview_source_InterviewSourcePPVParticipant)
          ?.stream?.id
      : undefined;

  const interviewGuests =
    stream.isInterview && stream.interview
      ? {
          interviewer: stream.interview.interviewer,
          interviewee: stream.interview.interviewee,
        }
      : null;

  return (
    <div ref={rootRef}>
      <Section
        title=""
        backgroundTitle="stream"
        className={styles.streamPlaylist}
      >
        {playingClip ? (
          <StreamClipHero
            clip={playingClip}
            store={stream.store}
            shareUrlPath={`/${stream.store.slug}/streams/${stream.slug}?tab=highlights&clip=${playingClip.id}`}
            onClose={() => setPlayingClip(null)}
          />
        ) : (
          <>
            <StreamVideoPlayer
              stream={stream}
              recordsList={recordsList}
              refetchStream={refetchStream}
              currentVideoUrl={currentVideoUrl}
              onVideoChange={handleVideoChange}
            />

            <div className={styles.contentLayout}>
              <div className={styles.infoWrapper}>
                <FinishedStreamInfo
                  stream={stream}
                  onTipModalToggle={handleStoreTipModalToggle}
                />
              </div>

              <VideoPlaylist
                recordsList={recordsList}
                currentVideoIndex={currentVideoIndex}
                onRecordClick={handleRecordClick}
              />
            </div>
          </>
        )}
      </Section>
      {stream && (
        <div className={styles.contentSections}>
          <StreamContentSections
            streamId={stream.id}
            streamName={stream.name || ''}
            streamUrl={`${getEnvLink()}/${stream.store.slug}/streams/${
              stream.slug
            }`}
            streamStartDate={stream.scheduleDateUtc}
            hasPrice={stream.price > 0}
            fanPollingEnabled={Boolean(stream.fanPollingEnabled)}
            sharePickGraphicsEnabled={Boolean(stream.sharePickGraphicsEnabled)}
            postEventContentHubEnabled={Boolean(
              stream.postEventContentHubEnabled
            )}
            isVotingOpen={stream.streamStatus === StreamStatus.Scheduled}
            streamSlug={stream.slug}
            storeId={stream.store.id}
            storeSlug={stream.store.slug || undefined}
            isStreamOver={
              stream.streamStatus === StreamStatus.Ended ||
              stream.streamStatus === StreamStatus.Interrupted
            }
            hasParticipants={
              interviewGuests
                ? Boolean(
                    interviewGuests.interviewer || interviewGuests.interviewee
                  )
                : Boolean(stream.participants?.length)
            }
            isInterview={stream.isInterview}
            interviewGuests={interviewGuests}
            onPlayHighlight={setPlayingClip}
            playingHighlightId={playingClip?.id ?? null}
          />
        </div>
      )}

      <OtherStreamsSection
        streams={streams}
        seeAllLink={`/${stream?.store?.slug}/streams`}
        seeAllLinkTitle="See Channel"
        className={styles.relatedStreamsSection}
        ppvStreamId={ppvStreamId}
      />

      <Modal
        open={storeTipModalIsOpen}
        onClose={handleCloseTipModal}
        size="small"
        className={styles.tipModal}
        showCloseIcon={false}
        blockScroll={false}
        containerClassName={styles.tipModalContainer}
        focusTrapped={false}
      >
        <div className={styles.tipModalContent}>
          <div className={styles.tipModalHeader}>
            <Text variant="h6" className={styles.tipModalHeaderTitle}>
              Tip {stream.store.storeDetails?.storeName}
            </Text>
            <Button
              variant="secondary"
              subVariant="text"
              size="small"
              icon="close"
              onClick={handleCloseTipModal}
              aria-label="Close"
              className={styles.tipModalCloseButton}
            />
          </div>
          <div
            className={styles.tipModalScrollableContent}
            role="region"
            aria-label="Tip form content"
          >
            <TipStoreV2 onCloseModal={handleDirectClose} />
          </div>
        </div>
      </Modal>

      <Modal
        open={showCancelModal}
        onClose={handleCancelCancel}
        size="small"
        showCloseIcon={false}
      >
        <div className={styles.cancelModal}>
          <Text variant="h3" textAlign="center" className={styles.cancelTitle}>
            Cancel Tip?
          </Text>

          <Text
            variant="body1Regular16"
            textAlign="center"
            color="light-white"
            className={styles.cancelDescription}
          >
            Are you sure you want to cancel this?
          </Text>

          <div className={styles.cancelButtons}>
            <Button
              type="button"
              variant="error"
              onClick={handleConfirmCancel}
              className={styles.cancelConfirmButton}
            >
              Yes, Cancel
            </Button>

            <Button
              type="button"
              variant="secondary"
              onClick={handleCancelCancel}
              className={styles.cancelBackButton}
            >
              Go Back
            </Button>
          </div>
        </div>
      </Modal>

      {showConfetti && (
        <Lottie
          animationData={confettiAnimation}
          loop={false}
          autoplay={true}
          onComplete={handleConfettiComplete}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 99999,
            pointerEvents: 'none',
          }}
        />
      )}
    </div>
  );
};

export default StreamsPlaylist;
